import { useRef, useState } from 'react';
import { haptic } from '../lib/native';

/**
 * 滑动确认。
 *
 * 只对**不可撤销的终态操作**用(送达)。滥用会让司机烦躁 ——
 * 每个操作都要滑,一天几十次。
 * 见 docs/14-driver-ux-spec.md §14.4
 */
export function SwipeConfirm({
  label,
  onConfirm,
  disabled,
  hint,
}: {
  label: string;
  onConfirm: () => void;
  disabled?: boolean;
  hint?: string;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [done, setDone] = useState(false);

  const KNOB = 56;
  const THRESHOLD = 0.7;

  function maxX(): number {
    return (trackRef.current?.clientWidth ?? 300) - KNOB - 8;
  }

  function start(clientX: number) {
    if (disabled || done) return;
    setDragging(true);
    const startX = clientX - x;

    const move = (e: PointerEvent) => {
      const next = Math.max(0, Math.min(maxX(), e.clientX - startX));
      setX(next);
    };

    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      setDragging(false);
      setX((cur) => {
        if (cur >= maxX() * THRESHOLD) {
          setDone(true);
          // 触发时给震动反馈 —— 戴手套时这是唯一能确认"点到了"的信号
          void haptic('medium');
          onConfirm();
          return maxX();
        }
        return 0; // 未过阈值弹回
      });
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }

  const progress = x / Math.max(1, maxX());

  return (
    <div>
      <div
        ref={trackRef}
        className={`relative h-16 rounded-2xl overflow-hidden select-none touch-none
          ${disabled ? 'bg-slate-200 dark:bg-slate-700 opacity-50' : 'bg-green-100 dark:bg-green-950/50'}`}
      >
        {/* 进度条随滑动填充,给出明确的"还差多少"反馈 */}
        <div
          className="absolute inset-y-0 left-0 bg-green-500/25"
          style={{ width: `${x + KNOB}px` }}
        />
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <span
            className={`text-lg font-medium ${
              disabled
                ? 'text-slate-500'
                : 'text-green-800 dark:text-green-300'
            }`}
            style={{ opacity: 1 - progress * 0.8 }}
          >
            {done ? '提交中…' : label} →→
          </span>
        </div>
        <div
          onPointerDown={(e) => start(e.clientX)}
          // ★ 滑块必须也能用键盘/读屏操作。
          //   只能拖的控件等于把「送达」这个主操作对辅助技术完全关死 ——
          //   而防误触的目的用「按住两次确认」之外的方式也能达到:
          //   键盘路径要求显式按键,本来就不会被口袋里的误碰触发
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-disabled={disabled}
          onKeyDown={(e) => {
            if (disabled || done) return;
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            setDone(true);
            setX(maxX());
            void haptic('medium');
            onConfirm();
          }}
          className={`absolute top-1 left-1 grid place-items-center rounded-xl bg-white
                      dark:bg-slate-100 shadow-md text-2xl
                      focus:outline-none focus:ring-2 focus:ring-green-600
                      ${disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
          style={{
            width: KNOB,
            height: KNOB,
            transform: `translateX(${x}px)`,
            transition: dragging ? 'none' : 'transform 0.2s ease-out',
          }}
        >
          {done ? '✓' : '▶'}
        </div>
      </div>
      {hint && <p className="mt-2 text-center text-sm text-amber-600 dark:text-amber-400">{hint}</p>}
    </div>
  );
}
